// Боссы: сложные боты с лицом и одной уникальной пассивкой. Лицо — колдун-планшет оригинала (десять колдунов,
// названных на картах фамильяров), фамильяр босса — «его» фамильяр (при подготовке ему предлагается только он),
// характер фиксирован. Пассивка привязана к фамильяру по смыслу и подключена через те же точки, что жетоны
// свойств (hooks.ts → bossPassive). Ядро имён боссов не знает: только реестр BOSSES и Player.boss.
// Сила пристреливается ареной (tools/boss-arena.ts): цель 30–40 % побед против трёх обычных сложных ботов
// (честная доля 25 %); перелёт режется числом в пассивке, не переработкой.
// Новый босс = запись здесь + RULES_VERSION + 1 (поле v — с какой версии он есть).
import type { BossId, BotStyle, CardDef, CardId, Game, Player, ScoreLine } from '../types.ts';
import type { EndGameCtx } from './hooks.ts';
import { STARTER_DECK } from './rules.ts';

/** Пассивка босса: каждый пункт — одна точка подключения в hooks.ts/game.ts */
export interface BossPassive {
  /** скидка на карту барахолки */
  discount?(def: CardDef): number;
  /** добавка к урону атак его палочек */
  wandDamageBonus?: number;
  /** первую атаку в его ход нельзя избежать */
  firstAttackUnavoidable?: boolean;
  /** все его атаки нельзя избежать */
  allAttacksUnavoidable?: boolean;
  /** добавка к урону всех его атак */
  attackDamageBonus?: number;
  /** чипсина за первую в его ход атаку, нанёсшую урон */
  chipsOnHit?: number;
  /** в начале хода берёт карту, если он последний по очкам (последняя электричка догоняет) */
  catchUpDraw?: boolean;
  /** скидка на палочки */
  wandDiscount?: number;
  /** ПО за легенду в конце игры */
  vpPerLegend?: number;
  /** каждый второй его ход враг-лидер по очкам, если он впереди босса, получает вялую палочку */
  limpToLeader?: boolean;
  /** жизни в начале и после смерти */
  life?: number;
  /** чипсины в начале хода — только пока их меньше chipsCap */
  chipsCap?: number;
  /** Грибуль ест и знаки, не только пшики и вялые палочки */
  trashSigns?: boolean;
  /** добавка к размеру руки */
  handBonus?: number;
  /** чипсин в начале каждого его хода */
  chipsPerTurn?: number;
  /** в начале хода верхняя карта колоды, если это пшик или вялая палочка, уходит в сброс */
  trashTopJunk?: boolean;
  /** вялые палочки не штрафуют в конце игры */
  limpNoPenalty?: boolean;
  /** строка очков в конце игры */
  endGameVP?(G: Game, p: Player, ctx: EndGameCtx): ScoreLine | null;
}

export interface BossDef {
  id: BossId;
  /** полное имя колдуна (с карты фамильяра) */
  name: string;
  /** короткое имя для аватара и ленты */
  short: string;
  familiar: CardId;
  style: BotStyle;
  /** текст пассивки для карточки босса */
  text: string;
  passive: BossPassive;
  /** версия правил, с которой босс существует */
  v: number;
}

export const BOSSES: Record<BossId, BossDef> = {
  netotded: {
    id: 'netotded', name: 'Нетотдед! Бородатая сволочь', short: 'Нетотдед', familiar: 'malchik-kotoryj-vyzhral', style: 'aggressor',
    text: 'Палочки стоят ему на 2 дешевле, а их атаки наносят на 2 урона больше.',
    passive: { wandDamageBonus: 2, wandDiscount: 2 }, v: 23,
  },
  benzoruk: {
    id: 'benzoruk', name: 'Бензорук Магокрошитель', short: 'Бензорук', familiar: 'benzopila-druzhok', style: 'aggressor',
    text: 'Первая в его ход атака, нанёсшая урон, приносит ему чипсину.',
    passive: { chipsOnHit: 1 }, v: 23,
  },
  hryuchevo: {
    id: 'hryuchevo', name: 'Адское Хрючево, Злак Маньяк', short: 'Хрючево', familiar: 'zozhlozhniki', style: 'maniac',
    text: 'Твари стоят ему на 1 дешевле.',
    passive: { discount: (def) => (def.type === 'тварь' ? 1 : 0) }, v: 23,
  },
  babakatya: {
    id: 'babakatya', name: 'Баба Катя, библиотекарша из ада', short: 'Баба Катя', familiar: 'muertito-knizhnyj-cherv', style: 'vengeful',
    text: 'Каждый жетон дохлого колдуна штрафует его на 1 меньше.',
    passive: { endGameVP: (_G, p) => (p.ddk.length ? { label: `Библиотекарша: жетоны ×${p.ddk.length}`, value: p.ddk.length } : null) }, v: 23,
  },
  yazhmuzhik: {
    id: 'yazhmuzhik', name: 'Яжмужик, сын Яжотца', short: 'Яжмужик', familiar: 'myzhsemya', style: 'hoarder',
    text: 'Легенды стоят ему на 1 дешевле.',
    passive: { discount: (def) => (def.legend ? 1 : 0) }, v: 23,
  },
  kimonyasha: {
    id: 'kimonyasha', name: 'Кимоняша, идол отаку', short: 'Кимоняша', familiar: 'schastlivyj-viabu', style: 'hoarder',
    text: 'В конце игры +2 ПО за каждую его легенду.',
    passive: { vpPerLegend: 2, endGameVP: (_G, _p, ctx) => (ctx.legends ? { label: `Идол отаку: легенды ×${ctx.legends}`, value: 2 * ctx.legends } : null) }, v: 23,
  },
  tanatych: {
    id: 'tanatych', name: 'Паровоз Танатыч, последняя электричка', short: 'Танатыч', familiar: 'konduktor-zhmi-na-tormoza', style: 'maniac',
    text: 'В начале хода берёт карту, если он последний по очкам.',
    passive: { catchUpDraw: true }, v: 23,
  },
  korol: {
    id: 'korol', name: 'Король Похоти, Гнилой Стручок', short: 'Король Похоти', familiar: 'salnyj-shut', style: 'vengeful',
    text: 'Каждый второй ход лидер, обогнавший его, получает вялую палочку; свои вялые палочки его не штрафуют.',
    passive: { limpNoPenalty: true, limpToLeader: true }, v: 23,
  },
  gribul: {
    id: 'gribul', name: 'Грибуль Дюк, доктор споровых наук', short: 'Грибуль Дюк', familiar: 'poehavshij-notari-us', style: 'hoarder',
    text: 'В начале его хода затравка или вялая палочка с верха колоды уходит в сброс.',
    passive: { trashTopJunk: true, trashSigns: true }, v: 23,
  },
  princessa: {
    id: 'princessa', name: 'Бедовая Принцесса, сильная и независимая', short: 'Принцесса', familiar: 'nikchemuhazhery', style: 'aggressor',
    text: 'В начале каждого его хода +1 чипсина, пока их меньше трёх.',
    passive: { chipsPerTurn: 1, chipsCap: 3 }, v: 23,
  },
};

export const BOSS_IDS: readonly BossId[] = Object.keys(BOSSES) as BossId[];

export const isBossId = (x: unknown): x is BossId => typeof x === 'string' && x in BOSSES;

export const bossOf = (p: Pick<Player, 'boss'>): BossDef | null => (p.boss ? BOSSES[p.boss] ?? null : null);

export const bossPassive = (p: Pick<Player, 'boss'>): BossPassive | null => bossOf(p)?.passive ?? null;

/** Имя босса за столом: корона и короткое имя */
export const bossName = (id: BossId): string => `👑 ${BOSSES[id].short}`;

/** Хлам для Грибуля: пшик и вялая палочка */
export function isJunk(def: CardDef, signs = false): boolean {
  return def.id === STARTER_DECK.blank || def.type === 'вялая палочка' || (signs && def.id === STARTER_DECK.sign);
}
